'use client';

import * as React from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslations } from 'next-intl';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { SustainabilityGovernanceFocusSlide } from '@/types/sustainability';

type GovernanceSlide = {
  id: string;
  imageSrc: string;
  imageAlt: string;
  title: string;
  bodyHtml: string;
  eyebrow?: string;
};

export interface SustainabilityGovernanceFocusSequenceProps {
  cmsSlides?: SustainabilityGovernanceFocusSlide[];
}

export default function SustainabilityGovernanceFocusSequence({
  cmsSlides,
}: SustainabilityGovernanceFocusSequenceProps = {}) {
  const t = useTranslations('sustainability.pages.governance');
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const pinRef = React.useRef<HTMLDivElement>(null);

  const fallbackImages = [
    '/images/stand_gov1.png',
    '/images/stand_gov2.png',
    '/images/stand_img1.jpg',
    '/images/stand_img2.jpg',
    '/images/stand_img3.jpg',
  ];

  const slides: GovernanceSlide[] = React.useMemo(() => {
    if (cmsSlides && cmsSlides.length > 0) {
      return cmsSlides.map((slide, idx) => ({
        id: slide.id,
        imageSrc: slide.imageUrl ?? fallbackImages[idx % fallbackImages.length],
        imageAlt: slide.imageAlt,
        title: slide.title,
        bodyHtml: slide.bodyHtml,
        eyebrow: slide.eyebrow,
      }));
    }
    return [
      {
        id: 'governance-1',
        imageSrc: '/images/stand_gov1.png',
        imageAlt: t('focusSlide1Alt'),
        title: t('focusSlide1Title'),
        bodyHtml: t('focusSlide1Body'),
      },
      {
        id: 'governance-2',
        imageSrc: '/images/stand_gov2.png',
        imageAlt: t('focusSlide2Alt'),
        title: t('focusSlide2Title'),
        bodyHtml: t('focusSlide2Body'),
      },
      {
        id: 'governance-3',
        imageSrc: '/images/stand_img1.jpg',
        imageAlt: t('focusSlide3Alt'),
        title: t('focusSlide3Title'),
        bodyHtml: t('focusSlide3Body'),
      },
      {
        id: 'governance-4',
        imageSrc: '/images/stand_img2.jpg',
        imageAlt: t('focusSlide4Alt'),
        title: t('focusSlide4Title'),
        bodyHtml: t('focusSlide4Body'),
      },
      {
        id: 'governance-5',
        imageSrc: '/images/stand_img3.jpg',
        imageAlt: t('focusSlide5Alt'),
        title: t('focusSlide5Title'),
        bodyHtml: t('focusSlide5Body'),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmsSlides, t]);

  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    gsap.registerPlugin(ScrollTrigger);

    const wrapper = wrapperRef.current;
    const pinEl = pinRef.current;
    if (!wrapper || !pinEl) return;

    const panelEls = Array.from(pinEl.querySelectorAll<HTMLElement>('[data-gov-panel]'));
    const mainEls = Array.from(pinEl.querySelectorAll<HTMLElement>('[data-gov-main]'));
    const textEls = Array.from(pinEl.querySelectorAll<HTMLElement>('[data-gov-text]'));
    const eyebrowEls = Array.from(pinEl.querySelectorAll<HTMLElement>('[data-gov-eyebrow]'));
    const headingEls = Array.from(pinEl.querySelectorAll<HTMLElement>('[data-gov-heading]'));
    const bodyEls = Array.from(pinEl.querySelectorAll<HTMLElement>('[data-gov-body]'));

    if (
      !panelEls.length ||
      !mainEls.length ||
      !textEls.length ||
      !eyebrowEls.length ||
      !headingEls.length ||
      !bodyEls.length
    ) {
      return;
    }

    gsap.set(panelEls, { autoAlpha: 0 });
    gsap.set(panelEls[0], { autoAlpha: 1 });
    gsap.set(eyebrowEls, { autoAlpha: 0, y: 24 });
    gsap.set(headingEls, { autoAlpha: 0, y: 36 });
    gsap.set(bodyEls, { autoAlpha: 0, y: 46 });
    gsap.set([eyebrowEls[0], headingEls[0], bodyEls[0]], { autoAlpha: 1, y: 0 });

    gsap.set(mainEls, { xPercent: -50, yPercent: -50, x: 0, y: 0, scale: 1, autoAlpha: 1 });

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: 'top-=120 top',
          end: `+=${(slides.length - 1) * 150}%`,
          pin: pinEl,
          scrub: 1.25,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      for (let i = 0; i < slides.length - 1; i += 1) {
        const currentPanel = panelEls[i];
        const nextPanel = panelEls[i + 1];
        const currentMain = mainEls[i];
        const nextMain = mainEls[i + 1];
        const currentEyebrow = eyebrowEls[i];
        const currentHeading = headingEls[i];
        const currentBody = bodyEls[i];
        const nextEyebrow = eyebrowEls[i + 1];
        const nextHeading = headingEls[i + 1];
        const nextBody = bodyEls[i + 1];

        tl.to(currentEyebrow, { autoAlpha: 0, y: -18, duration: 0.2, ease: 'power1.out' }, i)
          .to(
            currentHeading,
            { autoAlpha: 0, y: -30, duration: 0.24, ease: 'power1.out' },
            i + 0.08,
          )
          .to(currentBody, { autoAlpha: 0, y: -36, duration: 0.24, ease: 'power1.out' }, i + 0.12)
          .to(
            currentMain,
            {
              x: -360,
              y: -250,
              scale: 0.22,
              duration: 0.58,
              ease: 'power2.inOut',
            },
            i + 0.12,
          )
          .set(nextPanel, { autoAlpha: 1 }, i - 0.24)
          .fromTo(
            nextMain,
            {
              xPercent: -50,
              yPercent: -50,
              x: 350,
              y: 225,
              scale: 0.22,
              autoAlpha: 1,
            },
            { x: 0, y: 0, scale: 1, duration: 0.7, ease: 'power2.out' },
            i + 0.25,
          )
          .fromTo(
            nextEyebrow,
            { autoAlpha: 0, y: 24 },
            { autoAlpha: 1, y: 0, duration: 0.2, ease: 'power2.out' },
            i + 0.48,
          )
          .fromTo(
            nextHeading,
            { autoAlpha: 0, y: 40 },
            { autoAlpha: 1, y: 0, duration: 0.26, ease: 'power2.out' },
            i + 0.56,
          )
          .fromTo(
            nextBody,
            { autoAlpha: 0, y: 48 },
            { autoAlpha: 1, y: 0, duration: 0.28, ease: 'power2.out' },
            i + 0.64,
          )
          .set(currentPanel, { autoAlpha: 0 }, i + 0.95);
      }
    }, wrapper);

    const onResize = () => {
      ScrollTrigger.refresh();
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      ctx.revert();
    };
  }, [slides.length]);

  return (
    <div ref={wrapperRef} className="relative bg-black">
      {/* Mobile / tablet only: stacked slide list. */}
      <div className="bg-black px-4 py-6 sm:px-5 sm:py-7 md:px-6 md:py-8 lg:hidden">
        <div className="mx-auto flex max-w-[720px] flex-col gap-4 sm:gap-5">
          {slides.map((slide) => (
            <article
              key={`mobile-${slide.id}`}
              className="flex flex-col rounded-xl bg-[#161618] ring-1 ring-white/10"
            >
              <div className="relative w-full shrink-0 rounded-t-xl bg-[#0d0d0e]">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={slide.imageSrc}
                  alt={slide.imageAlt}
                  className="block h-auto w-full max-h-none object-contain object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="px-4 py-4 sm:px-5 sm:py-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#ff4b4b] sm:text-[11px]">
                  {slide.eyebrow || t('focusEyebrow')}
                </p>
                <h2 className="mt-1.5 text-[1.35rem] font-semibold leading-[1.12] tracking-[-0.01em] text-white sm:text-[1.5rem]">
                  {slide.title}
                </h2>
                <div
                  className="mt-2 text-[0.8125rem] leading-snug text-white/80 sm:text-sm [&_p]:m-0 [&_p+p]:mt-2 [&_strong]:font-semibold [&_strong]:text-white"
                  dangerouslySetInnerHTML={{ __html: slide.bodyHtml }}
                />
              </div>
            </article>
          ))}
        </div>
      </div>

      <div
        ref={pinRef}
        className="relative hidden h-[calc(100svh-150px)] w-full origin-center bg-black lg:block min-[1280px]:max-[1410px]:scale-75"
      >
        {slides.map((slide, i) => (
          <section key={slide.id} data-gov-panel className="absolute inset-0">
            <div
              data-gov-main
              className="absolute left-[42%] top-1/2 z-10 aspect-square w-[min(37vw,calc(100svh-150px),620px)] overflow-hidden rounded-[14px] shadow-[0_30px_60px_rgba(0,0,0,0.45)]"
            >
              <CmsImage
                src={slide.imageSrc}
                alt={slide.imageAlt}
                fill
                className="object-cover object-center"
                sizes="(max-width: 768px) 88vw, 52vw"
                priority={i < 2}
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
            </div>

            <div
              data-gov-text
              className="absolute right-[6%] top-1/2 z-20 w-[min(31vw,390px)] -translate-y-1/2"
            >
              <p
                data-gov-eyebrow
                className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ff4b4b]"
              >
                {slide.eyebrow || t('focusEyebrow')}
              </p>
              <h2
                data-gov-heading
                className="mt-3 text-4xl font-semibold leading-[1.08] tracking-[-0.02em] text-white"
              >
                {slide.title}
              </h2>
              <div
                data-gov-body
                className="mt-4 text-sm leading-relaxed text-white/75 sm:text-base [&_p]:m-0 [&_strong]:font-semibold [&_strong]:text-white"
                dangerouslySetInnerHTML={{ __html: slide.bodyHtml }}
              />
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
